import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, FileText, ShieldCheck, ArrowLeft, Search } from "lucide-react";

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const pathShown = useMemo(() => {
    const p = (location.pathname || "/") + (location.search || "");
    return p.length > 48 ? p.slice(0, 48) + "…" : p;
  }, [location.pathname, location.search]);

  const links = useMemo(
    () => [
      { to: "/", label: "Ana sayfa", icon: <Home size={18} /> },
      { to: "/indir", label: "Uygulamayı İndir", icon: <Home size={18} /> },
      {
        to: "/kullanim-kosullari",
        label: "Kullanım Koşulları",
        icon: <FileText size={18} />,
      },
      {
        to: "/gizlilik",
        label: "Gizlilik Politikası",
        icon: <ShieldCheck size={18} />,
      },
      {
        to: "/kvkk",
        label: "KVKK Aydınlatma Metni",
        icon: <ShieldCheck size={18} />,
      },
    ],
    []
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return links;
    return links.filter((x) => x.label.toLowerCase().includes(s));
  }, [q, links]);

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#f9fafb] via-white to-[#f1f1f1] pb-16 pt-24 sm:pt-28">
      <div className="mx-auto max-w-5xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-gray-700">
            <span className="font-semibold text-black">404</span>
            <span className="text-gray-400">•</span>
            <span>Sayfa bulunamadı</span>
          </div>

          <h1 className="mt-6 text-4xl font-extrabold leading-tight text-black md:text-5xl">
            Aradığın sayfa mevcut değil.
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-base text-gray-700 md:text-lg">
            Link hatalı olabilir ya da sayfa taşınmış olabilir. Aşağıdan hızlıca
            doğru sayfalara gidebilirsin.
          </p>

          <p className="mt-3 text-sm text-gray-500">
            İstediğin adres: <span className="font-mono">{pathShown}</span>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="mt-10 flex flex-col justify-center gap-4 sm:flex-row"
        >
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-black bg-white px-6 py-3 font-semibold text-black transition hover:bg-gray-50"
          >
            <ArrowLeft size={18} />
            Geri dön
          </button>

          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-black px-6 py-3 font-semibold text-white transition hover:opacity-90"
          >
            <Home size={18} />
            Ana sayfaya git
          </Link>

          <Link
            to="/indir"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-black px-6 py-3 font-semibold text-white transition hover:opacity-90"
          >
            Uygulamayı indir
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.16 }}
          className="mt-14"
        >
          <div className="mx-auto max-w-xl">
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
                size={18}
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Sayfa ara (örn. KVKK, Gizlilik, Kullanım Koşulları)"
                className="w-full rounded-md border border-gray-200 bg-white py-3 pl-11 pr-4 text-gray-800 focus:outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {filtered.map((x) => (
                <Link
                  key={x.to}
                  to={x.to}
                  className="group rounded-lg border-2 border-black bg-white p-5 transition hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-black">{x.icon}</div>
                    <div className="flex-1">
                      <div className="font-bold text-black">{x.label}</div>
                      <div className="mt-1 text-sm text-gray-600">
                        Açmak için tıkla
                      </div>
                    </div>
                    <div className="text-black opacity-60 transition group-hover:opacity-100">
                      →
                    </div>
                  </div>
                </Link>
              ))}

              {filtered.length === 0 && (
                <div className="col-span-1 rounded-lg border border-gray-200 bg-white p-5 text-center sm:col-span-2">
                  <p className="text-gray-700">
                    Bu aramaya uygun sayfa bulunamadı.
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    “KVKK”, “Gizlilik” ya da “Kullanım” diye deneyebilirsin.
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <div className="mt-14 text-center text-sm text-gray-500">
          Eğer bir hata olduğunu düşünüyorsan{" "}
          <a className="text-black underline" href="mailto:info@kolayyuk.com">
            info@kolayyuk.com
          </a>{" "}
          üzerinden bize yazabilirsin.
        </div>
      </div>
    </main>
  );
}